import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useFormFactor } from '../../hooks'
import {
  WHATS_NEW_COPY,
  WHATS_NEW_ENABLED,
  WHATS_NEW_ENTRY_SOURCE,
  WHATS_NEW_HOME_RAIL_MAX,
  isFresh,
  whatsNewPath
} from './constants'
import { useAnnouncements } from './hooks/useAnnouncements'
import { WhatsNewRailCard } from './whats-new-rail-card'

/**
 * The ambient entry point on the Home page: a rail of the notes published in
 * the last WHATS_NEW_FRESH_DAYS, and nothing at all when there are none — a
 * permanent "What's new" block that is usually stale would train people to
 * skip it. Titled "…in Mindtools" so it cannot be confused with the "What's
 * New" content rail that lists new articles.
 *
 * `Rails` is the host page's own rail component (paid and enterprise homes
 * each have one, with the same props), injected so this component doesn't
 * reach into `pages/`.
 */
const DATA_TEST = 'home-whats-new-product-rails'

export const WhatsNewHomeSection = ({ Rails }) => {
  const { isMobile } = useFormFactor()
  const { items, hasLoaded } = useAnnouncements({ eager: WHATS_NEW_ENABLED })

  const fresh = useMemo(
    () => items.filter(item => isFresh(item.publishedAt)).slice(0, WHATS_NEW_HOME_RAIL_MAX),
    [items]
  )

  if (!WHATS_NEW_ENABLED || !hasLoaded || fresh.length === 0) return null

  return (
    <Box sx={{ display: 'grid' }} data-test={`${DATA_TEST}-section`}>
      <Rails
        title={WHATS_NEW_COPY.homeRailTitle}
        items={fresh}
        dataTest={DATA_TEST}
        exploreMoreCta={(
          <Typography variant={!isMobile ? 'caption' : undefined}>
            <RouterLink
              to={whatsNewPath(WHATS_NEW_ENTRY_SOURCE.HOME)}
              data-test={`${DATA_TEST}-see-all`}
            >
              {WHATS_NEW_COPY.homeRailCta}
            </RouterLink>
          </Typography>
        )}
        renderItem={(item, itemIndex) => (
          <WhatsNewRailCard
            key={item.id}
            announcement={item}
            source={WHATS_NEW_ENTRY_SOURCE.HOME}
            dataTest={`${DATA_TEST.replace(/-rails$/, '')}-item-${itemIndex}`}
          />
        )}
      />
    </Box>
  )
}

WhatsNewHomeSection.propTypes = {
  Rails: PropTypes.elementType.isRequired
}

export default WhatsNewHomeSection
